// Input con label + error opcional. Envuelto en un contenedor `.field` que lleva
// el contorno de estado (idle/checking/valid/invalid) SEPARADO del input, para
// que el anillo de estado y el focus-ring convivan sin pelearse (ver index.css).
//
// Retrocompatible: sin `status`, un `invalid`/`error` deriva a 'invalid' y las
// demás páginas (login, forgot, reset…) se ven igual que antes salvo el contorno
// envolvente en lugar del viejo borde animado.

import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import {
  normalizeEmail,
  caretAfterNormalize,
  EMAIL_INPUT_ATTRS,
} from '@/lib/email-normalize';

// Estado visual del campo, derivado (en el signup) de la máquina. Unión cerrada:
// el consumidor lo pinta con un switch/estilo exhaustivo, igual que la lógica.
export type FieldStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'warning';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  // Marca el campo como inválido sin texto asociado (el detalle va en otro lado:
  // checklist de password, banner del form, etc.).
  invalid?: boolean;
  // Contorno envolvente explícito. Gana sobre `invalid`/`error`. 'idle' = sin anillo.
  status?: FieldStatus;
  // Botón de mostrar/ocultar (sólo tiene efecto en type="password").
  revealToggle?: boolean;
  // Contenido renderizado DENTRO del wrapper (ej: checklist de password), para que
  // el anillo de éxito envuelva input + contenido como un solo bloque.
  footer?: ReactNode;
  // Atributo `passwordrules` (spec de Apple) para que el password manager genere
  // una clave que ya cumpla la policy. No está en los tipos DOM → se castea.
  passwordRules?: string;
  // Sin marco propio: el campo va DENTRO de un contenedor agrupador (ej: los dos
  // emails en un solo `.field-group`), que lleva el anillo de estado por el
  // conjunto. Omite el `.field` (padding + anillo) de este input.
  frameless?: boolean;
}

function EyeIcon({ off }: { off: boolean }): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {off ? (
        <>
          <path d="M1.5 10S4.5 4 10 4c1.2 0 2.3.3 3.2.7M18.5 10S15.5 16 10 16c-1.2 0-2.3-.3-3.2-.7" />
          <path d="M3 3l14 14" />
        </>
      ) : (
        <>
          <path d="M1.5 10S4.5 4 10 4s8.5 6 8.5 6-3 6-8.5 6-8.5-6-8.5-6Z" />
          <circle cx="10" cy="10" r="2.5" />
        </>
      )}
    </svg>
  );
}

/**
 * Capas 1 y 2 del decreto "el email siempre en minúsculas" (ver
 * `@/lib/email-normalize`), aplicadas por el propio componente a TODO
 * `type="email"`.
 *
 * Va acá y no en cada página a propósito: es imposible agregar un campo de
 * email nuevo y olvidarse de blindarlo. El componente sigue siendo controlado —
 * sólo reescribe el `value` del evento antes de pasárselo al `onChange` del
 * padre, así que para la página no cambia nada.
 *
 * El manejo del cursor importa: sin él, corregir una letra en el medio de un
 * email largo patea el caret al final en cada tecla y el campo se vuelve
 * inusable.
 */
function useEmailGuards(
  isEmail: boolean,
  rest: InputHTMLAttributes<HTMLInputElement>,
): Partial<InputHTMLAttributes<HTMLInputElement>> {
  if (!isEmail) return {};
  return {
    ...EMAIL_INPUT_ATTRS,
    // Capa 1 — mientras tipea o pega: filtra el charset y baja a minúsculas.
    onChange: (e) => {
      const el = e.currentTarget;
      const raw = el.value;
      const clean = normalizeEmail(raw);
      if (clean !== raw) {
        const caret = el.selectionStart ?? raw.length;
        const next = caretAfterNormalize(raw, caret);
        el.value = clean;
        el.setSelectionRange(next, next);
      }
      rest.onChange?.(e);
    },
    // Capa 2 — al perder el foco: re-verifica y vuelve a forzar. Redundante con
    // la capa 1 salvo que algo haya escrito el valor por fuera del onChange
    // (autofill del password manager, extensión, restore del browser).
    onBlur: (e) => {
      const el = e.currentTarget;
      const clean = normalizeEmail(el.value);
      if (clean !== el.value) {
        el.value = clean;
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
      rest.onBlur?.(e);
    },
  };
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, error, invalid, status, revealToggle, footer, passwordRules, frameless, id, className = '', required, type, ...rest },
  ref,
) {
  const [revealed, setRevealed] = useState(false);
  const inputId = id ?? rest.name;
  // Se esparce DESPUÉS de `rest` en el <input>: los guards envuelven al
  // onChange/onBlur que pasó la página, así que tienen que ganarle.
  const emailGuards = useEmailGuards(type === 'email', rest);
  // Status efectivo: la prop gana; si no, un error/invalid deriva a 'invalid'.
  const effStatus: FieldStatus = status ?? (invalid || error ? 'invalid' : 'idle');
  const isInvalid = effStatus === 'invalid' || Boolean(error);
  const canReveal = Boolean(revealToggle) && type === 'password';
  const inputType = canReveal && revealed ? 'text' : type;
  return (
    <label
      htmlFor={inputId}
      className={`${frameless ? '' : 'field '}block text-sm space-y-1`}
      data-status={effStatus}
    >
      <span className="font-medium text-text">
        {label}
        {required ? (
          <span className="text-error ml-0.5" aria-hidden>
            *
          </span>
        ) : null}
      </span>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          required={required}
          type={inputType}
          className={`w-full min-h-[2.75rem] rounded-lg border border-text-muted/30 px-3 py-2 ${
            canReveal ? 'pr-11' : ''
          } bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent transition ${className}`}
          aria-invalid={isInvalid ? 'true' : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...rest}
          {...emailGuards}
          {...(passwordRules
            ? ({ passwordrules: passwordRules } as InputHTMLAttributes<HTMLInputElement>)
            : {})}
        />
        {canReveal ? (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            aria-pressed={revealed}
            aria-label={revealed ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 grid place-items-center h-8 w-8 rounded-md text-text-muted hover:text-text hover:bg-text-muted/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <EyeIcon off={revealed} />
          </button>
        ) : null}
      </div>
      {error ? (
        <span id={`${inputId}-error`} role="alert" className="text-xs text-error">
          {error}
        </span>
      ) : null}
      {footer}
    </label>
  );
});

export default FormField;
